import { useCallback, useEffect, useState } from 'react';
import type { PanelInicio, TarjetaHoy } from '../../api/ops/analitica';
import type { CifrasIndicador, Dimension, Indicador } from '../../api/ops/indicadores';
import type { AccesoDeInicio, Aviso, Contador, Navegacion } from '../../api/operaciones';
import { EstadoCargando } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { TusAccesos } from '../../componentes/TusAccesos';
import { accesosDeInicio, destinoDeAcceso } from '../../logica/navegacion';
import { useCabecera } from '../../marco/cabecera';
import { dibujoDe } from '../../marco/iconosMenu';
import { usePermisos } from '../../permisos/permisos';
import { mejorSiSube, nombreDimension, sumarDias, texto, tonoDelCambio } from '../indicadores/reglas';
import type { PropsPantalla } from '../tipos';
import { CambiarAccesos } from './CambiarAccesos';
import { LoProximo } from './LoProximo';
import { anilloDePlazas, eventoEnCurso, indicadorDePlazas, proximoDeCitas, proximoDeEvento, proximoDeReservas, rutaDeVerMas, type Proximo } from './proximo';
import {
    datoVivo,
    fechaLarga,
    fraseDelTitulo,
    funciones,
    horasPorProfesional,
    indicadoresDeFunciones,
    lineasDeConsumo,
    ordenarDeBuenoAMalo,
    paraRevisar,
    textoCambio,
} from './reglas';
import './inicio.css';

/**
 * Inicio (7.1), la «pantalla de calma»: frase positiva con el nombre del negocio, «Lo que Reservaria hace por ti» (los
 * accesos `feature` que declaran los módulos, G800, con el dato vivo de su indicador), «Tus accesos» (G129), «Hoy»
 * (`getTodayCard`, también para Recepción, G774), «Esta semana» frente a la anterior (`getDashboard`, G430), las horas
 * reservadas de cada profesional en un negocio de citas (G799, sin %), consumo del plan (`getUsage`, solo con `plan.ver`) y
 * «Para revisar» (`listMyNotices`). Cada bloque sale solo si llega su cifra: un 403 o un bloque sin contratar lo oculta.
 * «Lo próximo» (`LoProximo`): el evento de hoy ya empezado con sus llegadas por hora, o las reservas o las citas de hoy
 * por hora. «Cambiar accesos» (G129) guarda `home_shortcuts` (`CambiarAccesos`).
 */
interface Datos {
    nav: Navegacion | null;
    catalogo: Indicador[];
    proximo: Proximo | null;
    accesos: AccesoDeInicio[];
    tusAccesos: AccesoDeInicio[];
    hoy: TarjetaHoy | null;
    panel: PanelInicio | null;
    vivos: Record<string, CifrasIndicador>;
    profesionales: CifrasIndicador | null;
    consumo: Contador[];
    avisos: Aviso[];
}

const deFecha = (d: Date): string => `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** La dimensión del profesional, por su nombre en el panel (sin escribir la clave, que coincide con la de un rol). */
const esDeProfesional = (d: Dimension): boolean => nombreDimension(d) === 'Profesional';

async function oNada<T>(promesa: Promise<T>): Promise<T | null> {
    try {
        return await promesa;
    } catch {
        return null; // sin permiso, sin bloque o sin el módulo: el bloque no sale
    }
}

const ZONA_NAVEGADOR = Intl.DateTimeFormat().resolvedOptions().timeZone;

/** Todas las páginas de una lista (como mucho 5, 500 filas: lo de un día). */
async function paginas<T>(pedir: (cursor: string | null) => Promise<{ data: T[]; next_cursor?: string | null }>): Promise<T[]> {
    const todo: T[] = [];
    let cursor: string | null = null;
    for (let i = 0; i < 5; i += 1) {
        const p = await pedir(cursor);
        todo.push(...p.data);
        cursor = p.next_cursor ?? null;
        if (cursor === null) {
            break;
        }
    }
    return todo;
}

const sinMilisegundos = (d: Date): string => d.toISOString().replace(/\.\d{3}Z$/, 'Z');

export function Inicio({ ops, negocio, nombreNegocio, navegar, membresia }: PropsPantalla) {
    const { puede, ambito } = usePermisos();
    const [datos, setDatos] = useState<Datos | null>(null);
    const [editando, setEditando] = useState(false);
    const [hayGuardados, setHayGuardados] = useState(false);
    useCabecera({ ayuda: [{ icono: 'house', titulo: 'Inicio', texto: 'Lo importante de tu negocio de un vistazo. Cada tarjeta te lleva a su detalle.' }], acciones: [] });

    const conHoy = puede('analitica.hoy.ver');
    const conPanel = puede('analitica.panel.ver');
    const conPlan = puede('plan.ver');
    const conEventos = puede('entradas.eventos.ver');
    const conReservas = puede('reservas.reservas.ver');
    const conCitas = puede('citas.agenda.ver');
    const citasPropias = ambito('citas.agenda.ver') === 'own';
    const zonaMembresia = membresia?.timezone;

    const cargar = useCallback(async (): Promise<Datos> => {
        const hasta = deFecha(new Date());
        const desde = sumarDias(hasta, -6);
        const [nav, hoy, panel, avisos, consumo, catalogo] = await Promise.all([
            oNada(ops.getMyNavigation(negocio)),
            conHoy ? oNada(ops.getTodayCard(negocio)) : Promise.resolve(null),
            conPanel ? oNada(ops.getDashboard(negocio, 'last_7_days')) : Promise.resolve(null),
            oNada(ops.listMyNotices(negocio, { state: 'unread' })),
            conPlan ? oNada(ops.getUsage(negocio)) : Promise.resolve(null),
            conPanel ? oNada(ops.listIndicators(negocio, { limit: 100 })) : Promise.resolve(null),
        ]);
        const accesos = nav?.shortcuts ?? [];
        const legibles = new Set((catalogo?.data ?? []).map((i: Indicador) => i.code));
        const vivos: Record<string, CifrasIndicador> = {};
        await Promise.all(
            indicadoresDeFunciones(accesos)
                .filter((c) => legibles.has(c))
                .map(async (codigo) => {
                    const cifras = await oNada(ops.queryIndicator(negocio, codigo, { from: desde, to: hasta, granularity: 'day' }));
                    if (cifras !== null) {
                        vivos[codigo] = cifras;
                    }
                }),
        );
        // Horas reservadas por profesional (G799): el indicador en minutos que se desglosa por profesional, sin código fijo.
        const deProfesionales = (catalogo?.data ?? []).find((i: Indicador) => i.unit === 'minutes' && i.dimensions.some(esDeProfesional));
        const profesionales =
            deProfesionales === undefined ? null : await oNada(ops.queryIndicator(negocio, deProfesionales.code, { from: desde, to: hasta, granularity: 'day', breakdown: deProfesionales.dimensions.find(esDeProfesional) }));
        // «Lo próximo»: el evento de hoy ya empezado; si no, las reservas de hoy; si no, las citas de hoy (7.1).
        const ahora = new Date();
        const zona = zonaMembresia ?? hoy?.timezone ?? ZONA_NAVEGADOR;
        const dia = hoy?.local_date ?? deFecha(ahora);
        let proximo: Proximo | null = null;
        if (conEventos) {
            const eventos = await oNada(ops.listEvents(negocio, { status: ['published'], from: sinMilisegundos(new Date(ahora.getTime() - 86_400_000)), to: sinMilisegundos(ahora), limit: 25 }));
            const evento = eventoEnCurso(eventos?.data ?? [], ahora);
            if (evento !== null) {
                const lecturas = evento.access_group_id == null ? null : await oNada(ops.getScanSummary(negocio, evento.access_group_id));
                proximo = proximoDeEvento(evento, lecturas, ahora, zona);
            }
        }
        if (proximo === null && conReservas) {
            const reservas = await oNada(paginas((cursor) => ops.listReservations(negocio, { service_date: dia, limit: 100, cursor })));
            // «Plazas de hoy» (G864): el indicador de papel `occupancy`; sin él o sin permiso, no sale.
            const plazas = indicadorDePlazas(catalogo?.data ?? []);
            const ocupacion = reservas === null || plazas === null ? null : await oNada(ops.queryIndicator(negocio, plazas, { from: dia, to: dia, granularity: 'day' }));
            proximo = reservas === null ? null : proximoDeReservas(reservas, ahora, zona, anilloDePlazas(ocupacion));
        }
        if (proximo === null && conCitas) {
            const citas = await oNada(paginas((cursor) => ops.listAppointments(negocio, { from: dia, to: dia, limit: 100, cursor })));
            proximo = citas === null ? null : proximoDeCitas(citas, ahora, zona, citasPropias);
        }
        return {
            nav,
            catalogo: catalogo?.data ?? [],
            proximo,
            accesos,
            tusAccesos: nav === null ? [] : accesosDeInicio(nav).filter((a) => a.kind !== 'feature'),
            hoy,
            panel,
            vivos,
            profesionales,
            consumo: consumo?.data ?? [],
            avisos: avisos?.data ?? [],
        };
    }, [ops, negocio, conHoy, conPanel, conPlan, conEventos, conReservas, conCitas, citasPropias, zonaMembresia]);

    useEffect(() => {
        let vigente = true;
        void cargar().then((nuevos) => {
            if (vigente) {
                setDatos(nuevos);
            }
        });
        return () => {
            vigente = false;
        };
    }, [cargar]);

    if (datos === null) {
        return <EstadoCargando seccion="Inicio" />;
    }
    const d = datos;
    const subeBien = (codigo: string) => mejorSiSube(d.catalogo.find((i) => i.code === codigo));
    const frase = fraseDelTitulo(nombreNegocio, d.panel, d.hoy, subeBien);
    const fun = funciones(d.accesos);
    const semana = ordenarDeBuenoAMalo(d.panel?.cards.find((c) => c.code === 'period')?.items ?? [], subeBien);
    const otras = (d.panel?.cards ?? []).filter((c) => c.code !== 'period' && c.items.length > 0);
    const horas = horasPorProfesional(d.profesionales);
    const consumo = lineasDeConsumo(d.consumo);
    const revisar = paraRevisar(d.avisos);
    const personalizable = membresia !== undefined && d.nav !== null && d.nav.platform_access?.viewer !== 'platform';
    const tonoDe = (codigo: string, tono: 'sube' | 'baja' | 'igual') => tonoDelCambio(tono, subeBien(codigo));
    const releerAccesos = async () => {
        const nav = await ops.getMyNavigation(negocio);
        setDatos((x) => (x === null ? x : { ...x, nav, tusAccesos: accesosDeInicio(nav).filter((a) => a.kind !== 'feature') }));
    };
    const empezarACambiar = () => {
        setEditando(true);
        ops.getMyNavigationPreferences(negocio)
            .then((p) => {
                setHayGuardados(p.home_shortcuts != null);
            })
            .catch(() => undefined);
    };
    // Un acceso sin el bloque lleva a la entrada `plan` con ese bloque (G963); sin la entrada `plan`, a ninguna parte.
    const irAlAcceso = (a: AccesoDeInicio) => {
        const destino = d.nav === null ? null : destinoDeAcceso(d.nav, a);
        if (destino !== null) {
            navegar(destino);
        }
    };
    const abrirIndicador = (codigo: string, enlace?: string | null) => {
        navegar(enlace ?? `/indicadores?indicador=${encodeURIComponent(codigo)}`);
    };

    return (
        <div className="rv-inicio">
            <header className="rv-inicio__titulo">
                <h1>
                    {frase.antes}
                    <em>{frase.dato}</em>
                    {frase.despues}
                </h1>
                <p>
                    {d.hoy !== null ? fechaLarga(d.hoy.local_date) : fechaLarga(deFecha(new Date()))} · {frase.contexto}
                </p>
            </header>

            {fun.length > 0 && (
                <section className="rv-inicio__funciones" aria-labelledby="rv-inicio-funciones">
                    <h2 id="rv-inicio-funciones">Lo que Reservaria hace por ti</h2>
                    <div className="rv-inicio__rejilla-funciones">
                        {fun.map((f) => {
                            // Sin el bloque (solo al Propietario, G963): «+», el nombre en gris, sin dato y a la entrada `plan`.
                            const mas = f.not_contracted;
                            const vivo = f.indicator_code && !mas ? datoVivo(d.vivos[f.indicator_code]) : null;
                            return (
                                <button key={f.key} type="button" className={mas ? 'rv-funcion rv-funcion--mas' : 'rv-funcion'}
                                    aria-label={mas ? `${f.label}. ${f.description ?? ''}. No contratado: ver cómo activarlo` : undefined}
                                    onClick={() => {
                                        irAlAcceso(f);
                                    }}>
                                    <span className="rv-funcion__icono">
                                        <Icono nombre={dibujoDe(f.icon)} />
                                    </span>
                                    {mas && <i className="icon-plus rv-funcion__mas" aria-hidden="true" />}
                                    <strong>{f.label}</strong>
                                    {f.description ? <span className="rv-funcion__texto">{f.description}</span> : null}
                                    {vivo !== null && <span className="rv-funcion__dato">{vivo}</span>}
                                </button>
                            );
                        })}
                    </div>
                </section>
            )}

            {editando && d.nav !== null ? (
                <CambiarAccesos
                    navegacion={d.nav}
                    elegidos={d.tusAccesos}
                    hayGuardados={hayGuardados}
                    onGuardar={async (claves) => {
                        await ops.updateMyNavigationPreferences(negocio, { home_shortcuts: claves });
                        setHayGuardados(true);
                        await releerAccesos();
                    }}
                    onRestablecer={async () => {
                        await ops.updateMyNavigationPreferences(negocio, { home_shortcuts: null });
                        setHayGuardados(false);
                        await releerAccesos();
                    }}
                    onListo={() => {
                        setEditando(false);
                    }}
                />
            ) : (
                <TusAccesos accesos={d.tusAccesos} onIr={irAlAcceso} onCambiar={personalizable ? empezarACambiar : null} />
            )}

            {d.proximo !== null && <LoProximo proximo={d.proximo} ruta={d.nav === null ? null : rutaDeVerMas(d.nav, d.proximo.verMas)} navegar={navegar} />}

            {d.hoy !== null && d.hoy.items.length > 0 && (
                <section className="rv-inicio__bloque" aria-labelledby="rv-inicio-hoy">
                    <h2 id="rv-inicio-hoy">Hoy</h2>
                    <div className="rv-inicio__cifras">
                        {d.hoy.items.map((i) => (
                            <button key={i.indicator} type="button" className="rv-inicio__cifra" onClick={() => {
abrirIndicador(i.indicator);
}} disabled={!conPanel}>
                                <span className="rv-inicio__valor">{new Intl.NumberFormat('es-ES').format(i.value)}</span>
                                <span>{i.label}</span>
                            </button>
                        ))}
                    </div>
                </section>
            )}

            {semana.length > 0 && (
                <section className="rv-inicio__bloque" aria-labelledby="rv-inicio-semana">
                    <h2 id="rv-inicio-semana">Esta semana</h2>
                    <div className="rv-inicio__cifras">
                        {semana.map((i) => {
                            const cambio = textoCambio(i);
                            return (
                                <button key={i.indicator} type="button" className="rv-inicio__cifra" onClick={() => {
abrirIndicador(i.indicator, i.detail_link);
}}>
                                    <span className="rv-inicio__valor">{texto(i.unit, i.current)}</span>
                                    <span>{i.label}</span>
                                    {cambio !== null && <span className={`rv-inicio__cambio rv-inicio__cambio--${tonoDe(i.indicator, cambio.tono)}`}>{cambio.texto}</span>}
                                </button>
                            );
                        })}
                    </div>
                </section>
            )}

            {horas.length > 0 && (
                <section className="rv-inicio__bloque" aria-labelledby="rv-inicio-profesionales">
                    <h2 id="rv-inicio-profesionales">Horas reservadas de cada profesional</h2>
                    <ul className="rv-inicio__lista">
                        {horas.map((h) => (
                            <li key={h.id}>
                                <span>{h.nombre}</span>
                                <strong>{h.horas}</strong>
                            </li>
                        ))}
                    </ul>
                    <p className="rv-inicio__nota">Últimos 7 días</p>
                </section>
            )}

            {otras.map((c) => (
                <section key={c.code} className="rv-inicio__bloque rv-inicio__bloque--pequeno" aria-label={c.title}>
                    <h2>{c.title}</h2>
                    <div className="rv-inicio__cifras">
                        {c.items.map((i) => (
                            <button key={i.indicator} type="button" className="rv-inicio__cifra" onClick={() => {
abrirIndicador(i.indicator, i.detail_link);
}}>
                                <span className="rv-inicio__valor">{texto(i.unit, i.current)}</span>
                                <span>{i.label}</span>
                            </button>
                        ))}
                    </div>
                </section>
            ))}

            {consumo.length > 0 && (
                <section className="rv-inicio__bloque rv-inicio__bloque--pequeno" aria-labelledby="rv-inicio-consumo">
                    <h2 id="rv-inicio-consumo">Consumo del plan</h2>
                    <ul className="rv-inicio__lista">
                        {consumo.map((c) => (
                            <li key={c.concepto}>
                                <span>{c.concepto}</span>
                                <strong>{c.texto}</strong>
                                <span className="rv-inicio__barra" aria-hidden="true">
                                    <span style={{ width: `${String(c.porcentaje)}%` }} />
                                </span>
                            </li>
                        ))}
                    </ul>
                    <button type="button" className="rv-inicio__enlace" onClick={() => {
navegar('/plan?apartado=consumo');
}}>
                        Ver consumo
                    </button>
                </section>
            )}

            {revisar.length > 0 && (
                <section className="rv-inicio__bloque rv-inicio__bloque--pequeno" aria-labelledby="rv-inicio-revisar">
                    <h2 id="rv-inicio-revisar">Para revisar</h2>
                    <ul className="rv-inicio__lista">
                        {revisar.map((a) => (
                            <li key={a.id} className={`rv-inicio__aviso rv-inicio__aviso--${a.severity}`}>
                                <span>{a.title ?? ''}</span>
                            </li>
                        ))}
                    </ul>
                    <button type="button" className="rv-inicio__enlace" onClick={() => {
navegar('/avisos');
}}>
                        Ver tus avisos
                    </button>
                </section>
            )}
        </div>
    );
}
